import React, { useState } from "react";
import styled from "styled-components";

import Avatar from "../avatar/Avatar";
import IconButton from "../button/IconButton";
import DateDisplay, { DATE_DISPLAY_TYPE } from "../date-display/DateDisplay";
import StatusBadge from "../status-badge/StatusBadge";
import type { OfferWizardEntry } from "./types";

interface DraftThreadItemProps {
  entry: OfferWizardEntry;
  isSelected?: boolean;
  onClick: () => void;
  onDiscard: () => void;
}

// === ThreadItem と同等のスタイル系（draft 用に枠と色を調整） ===

const ThreadItemContainer = styled.div<{
  $isSelected: boolean;
  $isHover: boolean;
  $isActive: boolean;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px 4px 8px 8px;
  cursor: ${({ $isSelected }) => ($isSelected ? "default" : "pointer")};
  user-select: none;
  background-color: ${({ $isSelected, $isActive, $isHover }) =>
    $isSelected
      ? "var(--soremo-border)"
      : $isActive
        ? "var(--soremo-border)"
        : $isHover
          ? "var(--soremo-background)"
          : "#ffffff"};
  transition: background-color 0.2s ease;
  border-bottom: 1px solid var(--soremo-border);
  border-left: 6px solid var(--soremo-border);
`;

const PrimaryRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const TitleText = styled.div.attrs({ className: "heading-spacing-13" })`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DateText = styled.div.attrs({ className: "label8 u-text-light-gray" })`
  flex-shrink: 0;
`;

const SecondaryRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

const ScopeText = styled.div.attrs({ className: "bodytext-11 u-line-height-100" })`
  color: var(--soremo-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const PlaceholderAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 43% 57% 43% 57% / 57% 43% 57% 43%;
  transform: rotate(-19deg);
  border: 1px dashed var(--soremo-light-gray);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .material-symbols-rounded {
    transform: rotate(19deg);
    color: var(--soremo-light-gray);
    font-size: 24px;
  }
`;

const DraftThreadItem: React.FC<DraftThreadItemProps> = ({
  entry,
  isSelected = false,
  onClick,
  onDiscard,
}) => {
  const [isHover, setIsHover] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const summary = entry.data.summary;
  const hasRecipient =
    !!summary.recipientLabel || !!entry.data.recipient.userId;

  const titleText = summary.recipientLabel ?? "新規オファー";

  // 下段に出すサブ情報。ThreadItem と同様、「ロール（スコープ）」形式で表示し、
  // どちらかしか無い場合はある方だけ、両方無ければ "—" を出す。
  const scopeText =
    summary.roleLabel && summary.scope
      ? `${summary.roleLabel}（${summary.scope}）`
      : summary.roleLabel
        ? summary.roleLabel
        : summary.scope
          ? summary.scope
          : "—";

  const renderAvatar = () => {
    if (!hasRecipient) {
      return (
        <PlaceholderAvatar>
          <span className="material-symbols-rounded">person</span>
        </PlaceholderAvatar>
      );
    }
    return (
      <Avatar
        size={48}
        src={summary.recipientAvatarUrl ?? undefined}
        name={summary.recipientName}
      />
    );
  };

  const handleDiscard = (e: React.MouseEvent<HTMLDivElement>) => {
    // 親のクリック（行選択）に伝播させない
    e.stopPropagation();
    onDiscard();
  };

  return (
    <ThreadItemContainer
      $isSelected={isSelected}
      $isHover={isHover}
      $isActive={isActive}
      onClick={onClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => {
        setIsHover(false);
        setIsActive(false);
      }}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
    >
      <PrimaryRow>
        {renderAvatar()}

        <Center>
          <TitleRow>
            <TitleText>{titleText}</TitleText>
            <DateText>
              <DateDisplay
                date={entry.lastSyncedAt ?? entry.createdAt}
                type={DATE_DISPLAY_TYPE.ADAPTIVE}
              />
            </DateText>
          </TitleRow>

          <SecondaryRow>
            <ScopeText>{scopeText}</ScopeText>
            <StatusBadge
              variant="inline"
              label="ドラフト作成中"
              highlighted
            />
          </SecondaryRow>
        </Center>

        <RightActions onClick={(e) => e.stopPropagation()}>
          <IconButton style={{ fontSize: 20 }} onClick={handleDiscard} icon="delete_outline" />
        </RightActions>
      </PrimaryRow>
    </ThreadItemContainer>
  );
};

export default DraftThreadItem;
